/**
 * Filter drawer for the API Credentials view.
 * Uses the shared FilterDrawer for the outer drawer shell.
 * Only contains the Organizations filter (SUPER_ADMIN only).
 */
import { Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { GraphQL } from "@rpi/openapi-api";
import { FilterDrawer, useFilterDrawerContext } from "../../components/filters/FilterDrawer";
import { SearchInput, toSearchInputArray } from "../../configuration/filters/searchInput";
import {
  getCustomerPublicIdFilter,
  getInitialOrganization,
  NO_FILTER_VALUE,
  OrganizationFilter,
} from "../../components/filters/OrganizationFilter";
import { useFilterStyles } from "../../components/filters/filterStyles";

export const path = "filter";
const filterFormId = "api-credentials-filter-form";

type ApiCredentialsFilterProps = {
  isOpened: boolean;
  filters: SearchInput<GraphQL.ApiCredentialSearchFields>[];
  onClose: () => void;
  onApply: (filter: SearchInput<GraphQL.ApiCredentialSearchFields>[]) => void;
};

export function ApiCredentialsFilter({ isOpened, filters, onClose, onApply }: ApiCredentialsFilterProps) {
  return (
    <FilterDrawer formId={filterFormId} isOpened={isOpened} filters={filters} onClose={onClose} onApply={onApply} />
  );
}

export function ApiCredentialsFilterInner() {
  const { classes } = useFilterStyles();
  const { initialFilters, onClose, onApply } = useFilterDrawerContext<GraphQL.ApiCredentialSearchFields>();

  const { getInputProps, onSubmit, setFieldValue } = useForm({
    initialValues: {
      selectedCustomer: getInitialOrganization(GraphQL.ApiCredentialSearchFields.CustomerPublicId, initialFilters),
    },
  });

  const handleApply = (customerPublicId: string) => {
    const filters = toSearchInputArray([
      getCustomerPublicIdFilter(GraphQL.ApiCredentialSearchFields.CustomerPublicId, customerPublicId),
    ]);
    onApply(filters);
    onClose();
  };

  const handleClear = () => {
    setFieldValue("selectedCustomer", NO_FILTER_VALUE);
  };

  return (
    <Stack className={classes.root}>
      <form
        id={filterFormId}
        className={classes.form}
        onSubmit={onSubmit((values) => handleApply(values.selectedCustomer))}
        onReset={handleClear}
      >
        <OrganizationFilter {...getInputProps("selectedCustomer")} />
      </form>
    </Stack>
  );
}
